import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { operatorAPI } from '../../lib/api';
import { 
  Users, Baby, UserCircle, School, Bell, CheckCircle, XCircle, 
  Mail, Package, AlertTriangle, TrendingUp, RefreshCw, Activity,
  Play, Clock, Server
} from 'lucide-react';

/**
 * Operator Dashboard Overview
 * Endpoints used:
 * - GET /api/operator/overview
 * - GET /api/operator/today
 * - GET /api/operator/debug/failures
 * - GET /api/operator/scheduler/status
 * - POST /api/operator/test-p2-jobs/run-full-day-simulation
 */
export const OperatorOverview = () => {
  const [overview, setOverview] = useState(null);
  const [todayOps, setTodayOps] = useState(null);
  const [failures, setFailures] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [overviewRes, todayRes, failuresRes, schedulerRes] = await Promise.all([
        operatorAPI.getOverview().catch(e => ({ data: { ok: false, error: e.message } })),
        operatorAPI.getToday().catch(e => ({ data: { ok: false, error: e.message } })),
        operatorAPI.getFailures(10).catch(e => ({ data: { ok: false, failures: [] } })),
        operatorAPI.getSchedulerStatus().catch(e => ({ data: { ok: false, error: e.message } })),
      ]);
      
      setOverview(overviewRes.data);
      setTodayOps(todayRes.data);
      setFailures(failuresRes.data?.failures || []);
      setSchedulerStatus(schedulerRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleRunSimulation = async () => {
    setSimulationRunning(true);
    setSimulationResult(null);
    try {
      const res = await operatorAPI.runFullDaySimulation();
      setSimulationResult(res.data);
    } catch (err) {
      setSimulationResult({ ok: false, error: err.message });
    } finally {
      setSimulationRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="operator-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const kpis = overview?.families || overview?.kpis?.families || {};
  const children = overview?.children || overview?.kpis?.children || {};
  const caregivers = overview?.caregivers || overview?.kpis?.caregivers || {};
  const schools = overview?.schools || overview?.kpis?.schools || {};
  const alerts = overview?.alerts || overview?.kpis?.alerts || {};
  const deliveries = overview?.deliveries_24h || overview?.kpis?.deliveries_24h || {};
  const today = todayOps?.today || {};

  const kpiCards = [
    { label: 'Total Families', value: kpis.total || 0, icon: Users, color: 'bg-blue-50 text-blue-600' },
    { label: 'Configured', value: kpis.configured || 0, icon: CheckCircle, color: 'bg-green-50 text-green-600' },
    { label: 'Active (7d)', value: kpis.active_7d || 0, icon: Activity, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Churn Risk', value: kpis.churn_risk || 0, icon: AlertTriangle, color: 'bg-amber-50 text-amber-600' },
    { label: 'Children', value: children.total || 0, icon: Baby, color: 'bg-pink-50 text-pink-600' },
    { label: 'Caregivers', value: caregivers.total || 0, icon: UserCircle, color: 'bg-purple-50 text-purple-600' },
    { label: 'Schools', value: schools.total || 0, icon: School, color: 'bg-indigo-50 text-indigo-600' },
    { label: 'Active Alerts', value: alerts.total || 0, icon: Bell, color: 'bg-red-50 text-red-600' },
  ];

  return (
    <div data-testid="operator-overview-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Operator Dashboard</h1>
          <p className="text-slate-500 mt-1">
            Runtime: {overview?.runtime_version || 'N/A'}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          data-testid="refresh-button"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="error-banner">
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="kpi-grid">
        {kpiCards.map((card, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{card.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{card.value}</p>
              </div>
              <div className={`p-2 rounded-lg ${card.color}`}>
                <card.icon className="w-5 h-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Today's Operations */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="today-ops-section">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Today's Operations
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Emails */}
          <div>
            <p className="text-sm text-slate-500 mb-2">Emails</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Mail className="w-4 h-4 text-blue-500" />
                <span className="font-semibold">{today.emails?.ingested || 0}</span>
                <span className="text-xs text-slate-400">ingested</span>
              </div>
              {(today.emails?.failed || 0) > 0 && (
                <div className="flex items-center gap-1 text-red-500">
                  <XCircle className="w-4 h-4" />
                  <span className="font-semibold">{today.emails.failed}</span>
                </div>
              )}
            </div>
          </div>

          {/* Nanny Packs */}
          <div>
            <p className="text-sm text-slate-500 mb-2">Nanny Packs</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Package className="w-4 h-4 text-green-500" />
                <span className="font-semibold">{today.nanny_packs?.generated || 0}</span>
                <span className="text-xs text-slate-400">generated</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="font-semibold">{today.nanny_packs?.sent || 0}</span>
                <span className="text-xs text-slate-400">sent</span>
              </div>
              {(today.nanny_packs?.failed || 0) > 0 && (
                <div className="flex items-center gap-1 text-red-500">
                  <XCircle className="w-4 h-4" />
                  <span className="font-semibold">{today.nanny_packs.failed}</span>
                </div>
              )}
            </div>
          </div>

          {/* Check-ins */}
          <div>
            <p className="text-sm text-slate-500 mb-2">Check-ins</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Activity className="w-4 h-4 text-purple-500" />
                <span className="font-semibold">{today.checkins?.attempted || 0}</span>
                <span className="text-xs text-slate-400">attempted</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="font-semibold">{today.checkins?.sent || 0}</span>
              </div>
              {(today.checkins?.failed || 0) > 0 && (
                <div className="flex items-center gap-1 text-red-500">
                  <XCircle className="w-4 h-4" />
                  <span className="font-semibold">{today.checkins.failed}</span>
                </div>
              )}
            </div>
          </div>

          {/* Reminders */}
          <div>
            <p className="text-sm text-slate-500 mb-2">Reminders</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Bell className="w-4 h-4 text-amber-500" />
                <span className="font-semibold">{today.reminders?.attempted || 0}</span>
                <span className="text-xs text-slate-400">attempted</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="font-semibold">{today.reminders?.sent || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 24h Delivery Summary */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-500">
            Last 24h: <span className="text-green-600 font-medium">{deliveries.success || 0} delivered</span>
            {(deliveries.failed || 0) > 0 && (
              <span className="text-red-500 ml-2 font-medium">{deliveries.failed} failed</span>
            )}
          </p>
        </div>
      </div>

      {/* Scheduler Status & Simulation Control */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Scheduler Status (Read-Only) */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="scheduler-status-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Scheduler Status
          </h2>
          
          {schedulerStatus ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  schedulerStatus.scheduler_running 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {schedulerStatus.scheduler_running ? 'Running' : 'Stopped'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Timezone</span>
                <span className="text-sm font-mono">{schedulerStatus.scheduler_timezone || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Current Time (IST)</span>
                <span className="text-sm font-mono">{schedulerStatus.current_ist_time || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Total Jobs</span>
                <span className="text-sm font-semibold">{schedulerStatus.total_jobs || 0}</span>
              </div>
              
              {/* Jobs List */}
              {schedulerStatus.jobs && schedulerStatus.jobs.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Registered Jobs</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {schedulerStatus.jobs.slice(0, 8).map((job, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 truncate max-w-[150px]">{job.job_name || job.name || job.job_id}</span>
                        <span className="text-slate-400 font-mono">
                          {job.next_run_time_ist ? job.next_run_time_ist.split(' ')[1]?.slice(0, 5) : (job.next_run ? new Date(job.next_run).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'N/A')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Loading scheduler status...</p>
          )}
        </div>

        {/* Simulation Control */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="simulation-control-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Play className="w-5 h-5" />
            Full Day Simulation
          </h2>
          
          <p className="text-sm text-slate-500 mb-4">
            Run all scheduled jobs in sequence to test the full day workflow. Creates execution_logs for all active families.
          </p>
          
          <button
            onClick={handleRunSimulation}
            disabled={simulationRunning}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              simulationRunning
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
            }`}
            data-testid="run-simulation-btn"
          >
            {simulationRunning ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Running Simulation...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Full Day Simulation
              </>
            )}
          </button>
          
          {/* Simulation Result */}
          {simulationResult && (
            <div className={`mt-4 p-3 rounded-lg ${
              simulationResult.ok 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`} data-testid="simulation-result">
              {simulationResult.ok ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-800">Simulation Complete</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-green-600">Logs Created:</span>
                      <span className="ml-1 font-semibold">{simulationResult.summary?.total_logs_created || simulationResult.total_logs_created || 0}</span>
                    </div>
                    <div>
                      <span className="text-green-600">Jobs Executed:</span>
                      <span className="ml-1 font-semibold">{simulationResult.summary?.jobs_executed || simulationResult.jobs_executed || 0}</span>
                    </div>
                    <div>
                      <span className="text-amber-600">Jobs Skipped:</span>
                      <span className="ml-1 font-semibold">{simulationResult.summary?.jobs_skipped || simulationResult.jobs_skipped || 0}</span>
                    </div>
                    <div>
                      <span className="text-red-600">Errors:</span>
                      <span className="ml-1 font-semibold">{simulationResult.summary?.total_errors || simulationResult.errors?.length || 0}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-red-700">{simulationResult.error || 'Simulation failed'}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link 
          to="/operator/families"
          className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md hover:border-primary/50 transition-all"
          data-testid="link-families"
        >
          <Users className="w-8 h-8 text-primary mb-3" />
          <h3 className="font-semibold text-slate-900">Manage Families</h3>
          <p className="text-sm text-slate-500 mt-1">View, search, and configure families</p>
        </Link>
        
        <Link 
          to="/operator/queue"
          className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md hover:border-amber-400/50 transition-all"
          data-testid="link-queue"
        >
          <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />
          <h3 className="font-semibold text-slate-900">Failures Queue</h3>
          <p className="text-sm text-slate-500 mt-1">Review and retry failed deliveries</p>
        </Link>
        
        <Link 
          to="/onboarding"
          className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md hover:border-green-400/50 transition-all"
          data-testid="link-onboarding"
        >
          <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
          <h3 className="font-semibold text-slate-900">New Family</h3>
          <p className="text-sm text-slate-500 mt-1">Onboard a new family step by step</p>
        </Link>
      </div>

      {/* Recent Failures */}
      {failures.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="failures-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Recent Failures
            </h2>
            <Link to="/operator/queue" className="text-sm text-primary hover:underline">
              View All
            </Link>
          </div>
          
          <div className="space-y-3">
            {failures.slice(0, 5).map((failure, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {failure.delivery_type || 'delivery'} failed
                  </p>
                  <p className="text-xs text-slate-500 truncate max-w-md">
                    {failure.error_message || 'Unknown error'}
                  </p>
                </div>
                <span className="text-xs text-slate-400">
                  {failure.created_at ? new Date(failure.created_at).toLocaleTimeString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State for Failures */}
      {failures.length === 0 && (
        <div className="bg-green-50 rounded-xl border border-green-200 p-6 text-center" data-testid="no-failures">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="font-semibold text-green-800">All Clear!</h3>
          <p className="text-sm text-green-600">No recent delivery failures.</p>
        </div>
      )}
    </div>
  );
};

export default OperatorOverview;
